import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { ImageSquare, Check } from '@phosphor-icons/react';
import { Dictionary } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useApiMutation } from 'theSource/utils';
import { POPUP_CONTENT_QUERY_KEY } from 'enrollment/features/ToDoList/utils';
import { OliveRecommends } from 'enrollment/features/OliveAdvisor/components/OliveRecommends';
import { closeSideDrawer, SpecificIcon } from 'enrollment/utils/common/Utils';
import {
  PlanCardHeaderSyled,
  CarrierLogoBoxStyled,
  PlanCardFooterStyled,
  AddToCompareStyled,
  PlanCardIneligibleBanner,
} from './PlanCardStyled';
import { PlanCardDetails } from './PlanCardDetails';
import {
  BenefitPlanDto,
  CoverageDto,
  PcpMembersDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  PopupContentResponseDto,
  PopupContentRequestDto,
  PopupContentDto,
} from 'enrollment/features/ToDoList/types';
import { buildElectionParams, handleCoverageLevelId } from './utils';
import { AdditionalPlanElectionDataDto, ElectRequestDto } from './types';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { RecommendationBanner } from './components';
import { DecisionIQSurveySubmissionDto } from 'enrollment/queries';
import { EsignatureModal } from '../EsignatureModal';
import './PlanCard.css';
import {
  FieldValueDto,
  FieldWithValueDto,
  ErrorResponseDto,
} from 'theSource/types';
import { PCPDrawer } from './components/PCPDrawer';
import { setHideSubscriberCheckboxWhenChecked } from './components/ChoosePCPDrawer/SearchResultsPCPDrawer/services';
import { usePlanInfoQuery } from 'enrollment/queries/usePlanInformationQuery';
import { selectLifeEventCompleted } from 'enrollment/services';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { PSBannerWrapper } from '../../../components/Shared';
import { openEsignatureModal } from '../EsignatureModal/tests/utils';
import { PsButtonTuner, PsCheckboxTuner } from 'stencilSetup';
import { RiderBenefits } from 'enrollment/components/RiderBenefits';
import { getPremiumValue } from '../planDetailsUtils';
import { PlanPopup, openPlanPopup } from './components/PlanPopup';
import { useConfigurationQuery } from 'theSource/queries';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

interface PlanCardProps {
  plan: BenefitPlanDto;
  orgPlanYearId: number;
  orgBenefitId?: number;
  enrollmentContextType?: string;
  subscriberProfile?: FieldWithValueDto;
  selectedDependentIds: number[];
  dependents?: DependentsResponseDto;
  selected: boolean;
  preElectedPlan?: boolean;
  disabled: boolean;
  color: string;
  benefitLookup: string;
  updateElection: (params: ElectRequestDto) => void;
  isRecommendedPlan?: boolean;
  onRecommendationSurveySubmit?: (arg0: DecisionIQSurveySubmissionDto) => void;
  setComparePlans?: any;
  disableCompareBtns?: boolean;
  showEsignatureModal?: boolean;
  pcpMembers?: PcpMembersDto | undefined;
  isDepSelect?: boolean;
  coverage?: CategoryCoverageDto | CoverageDto | undefined;
  parentCovLevel?: string;
  subscribeZipCode?: FieldValueDto | undefined;
  isOnlyPlan?: boolean;
  oliveRecommends?: boolean;
  reasons?: string;
  hideEmptyCost: boolean;
  confirmButtonOverrideLabel?: string;
}

interface BtnProps {
  onClick: () => void;
  disabled?: boolean;
}

export const PlanCard = ({
  plan,
  orgPlanYearId,
  orgBenefitId,
  enrollmentContextType,
  subscriberProfile,
  selectedDependentIds,
  dependents,
  selected,
  preElectedPlan,
  disabled,
  color,
  benefitLookup,
  updateElection,
  isRecommendedPlan,
  onRecommendationSurveySubmit,
  setComparePlans,
  disableCompareBtns = false,
  showEsignatureModal,
  pcpMembers,
  isDepSelect,
  coverage,
  parentCovLevel,
  subscribeZipCode,
  isOnlyPlan,
  oliveRecommends,
  reasons,
  hideEmptyCost,
  confirmButtonOverrideLabel,
}: PlanCardProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const lifeEventCompleted = useSelector(selectLifeEventCompleted);
  const orgPlanId = plan.plan_id;

  const [isElecting, setIsElecting] = useState(false);
  const [displayPopups, setDisplayPopups] = useState(false);
  const [addToCompareCheckbox, setAddToCompareCheckbox] = useState(false);
  const [comparePlansDetails, setComparePlansDetails] = useState<any>({
    plan: plan,
    add: false,
    remove: false,
  });
  const [showPcpDrawer, setShowPcpDrawer] = useState(false);

  const [openChooseProviderDrawer, setOpenChooseProviderDrawer] =
    useState(false);

  const [
    hideDependentCheckboxWhenChecked,
    setHideDependentCheckboxWhenChecked,
  ] = useState<{ [key: string]: boolean }>({});

  const [electedRiders, setElectedRiders] = useState<number[]>([]);

  const isFindAProviderEnabled = plan?.pcp_data?.find_a_provider_enabled; //dental find a provider config

  const isPcpRequired =
    plan.pcp_data?.pcp_name_setting === 'required' ||
    plan.pcp_data?.pcp_code_setting === 'required';

  const isPcpOptional =
    plan.pcp_data?.pcp_name_setting === 'optional' &&
    plan.pcp_data?.pcp_code_setting === 'optional';

  const hasPopupContent = plan.has_org_plan_popups;

  const [pcpDrawerRequired, setPcpDrawerRequired] = useState(isPcpRequired);
  const [planPopupRequired, setPlanPopupRequired] = useState(hasPopupContent);
  const [esignatureModalRequired, setEsignatureModalRequired] = useState(
    showEsignatureModal || false
  );

  const [popupContentData, setPopupContentData] = useState<PopupContentDto>();
  const showPlanPopup =
    popupContentData?.carrier_files?.length ||
    popupContentData?.files?.length ||
    popupContentData?.plan_popup_contents.length ||
    popupContentData?.plan_popup_files.length;

  useMemo(() => {
    setPlanPopupRequired(hasPopupContent && Boolean(showPlanPopup));
  }, [hasPopupContent, showPlanPopup]);

  const { configuration } = useConfigurationQuery();

  // value to be used when the pcp drawer is opened from the plan details, where making a change
  // to pcp data can trigger an election compared to when it is opened after initially electing a plan
  const [allowPcpElection, setAllowPcpElection] = useState(false);

  const [additionalElectionData, setAdditionalElectionData] = useState<
    AdditionalPlanElectionDataDto | undefined
  >({});

  const togglePcpDrawer = () => {
    setShowPcpDrawer(!showPcpDrawer);
  };

  const toggleDisplayPopups = (data: any) => {
    setDisplayPopups(data);
    closeSideDrawer();
  };

  const updatedCheckboxRef = useRef(addToCompareCheckbox);

  const coverageLevelLookupCode = coverage?.coverage_level_lookup_code || '';
  const { planInfoData, isLoadingPlanInfo, isFetchingPlanInfo } =
    usePlanInfoQuery({
      orgPlanYearId,
      enrollmentContext,
      lifeEventCompleted,
      orgPlanId,
      enabled: orgPlanId > 0,
    });
  const showCovLevSelect =
    !isLoadingPlanInfo &&
    !isFetchingPlanInfo &&
    !isDepSelect &&
    parentCovLevel === undefined;

  const [selectedCovLevel, setSelectedCovLevel] = useState(
    coverageLevelLookupCode
  );
  const origSelectedCovLevel = coverageLevelLookupCode;
  const covLevelSelectProps = {
    planInfoData,
    isLoadingPlanInfo,
    isFetchingPlanInfo,
    showCovLevSelect,
    selectedCovLevel: parentCovLevel || selectedCovLevel,
    setSelectedCovLevel,
  };

  const handleCompareCheckbox = (e: any) => {
    if (e.target.tagName === 'INPUT') {
      setAddToCompareCheckbox(e?.target?.checked);
      updatedCheckboxRef.current = e?.target?.checked;

      if (e?.target?.checked) {
        setComparePlansDetails(() => {
          return {
            plan: plan,
            add: true,
            remove: false,
          };
        });
      } else {
        setComparePlansDetails(() => {
          return {
            plan: plan,
            add: false,
            remove: true,
          };
        });
      }
    }
  };

  useEffect(() => {
    const addToCompareElements = document.querySelectorAll(
      'input[name="add_to_compare"]'
    );
    const compareCheckedCount = document.querySelectorAll(
      'input[name="add_to_compare"]:checked'
    )?.length;

    if (compareCheckedCount > 0) {
      if (compareCheckedCount >= 3) {
        addToCompareElements.forEach((element: any) => {
          if (!element.checked) {
            element.disabled = true;
          }
        });
      } else {
        addToCompareElements.forEach((element: any) => {
          element.disabled = false;
        });
      }
    }
    // while adding setComparePlans in the dependency array, it will cause an infinite loop
    // so we are disabling the eslint rule
    setComparePlans(comparePlansDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparePlansDetails]);

  useEffect(() => {
    if (parentCovLevel) {
      setSelectedCovLevel(parentCovLevel);
    }
  }, [parentCovLevel]);

  const { mutate: getPopupContent } = useApiMutation<
    PopupContentResponseDto,
    ErrorResponseDto,
    PopupContentRequestDto
  >({
    queryKey: POPUP_CONTENT_QUERY_KEY,
    method: 'post',
    endpoint: '/cart/coverages/popups',
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
    onSuccess: (data) => {
      setPopupContentData(data.data);
    },
  });

  const mapElectionData = (coverage?: CategoryCoverageDto | CoverageDto) => {
    return {
      increment: coverage?.req_increments || '',
      line_increments: '',
      coverage_level_id: coverage?.coverage_level_id || plan.level_id,
      org_plan_id: plan.plan_id || 0,
      dependent_ids: selectedDependentIds,
      subscriber_amount: Number(plan.subscriber_premium) || 0,
    };
  };

  useEffect(() => {
    if (plan.has_org_plan_popups) {
      getPopupContent({
        org_benefit_id: orgBenefitId,
        life_event_completed: lifeEventCompleted,
        enrollment_context_type: enrollmentContextType || '',
        election: mapElectionData(coverage),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage, plan.has_org_plan_popups]);

  const planPremium = getPremiumValue(
    Boolean(isDepSelect),
    plan,
    planInfoData,
    selectedCovLevel
  );

  const dispatch = useDispatch();

  const resetStateData = () => {
    setPcpDrawerRequired(isPcpRequired);
    setPlanPopupRequired(hasPopupContent);
    setEsignatureModalRequired(showEsignatureModal || false);
    setDisplayPopups(false);
    setAllowPcpElection(false);
    setIsElecting(false);
    setShowPcpDrawer(false);
    dispatch(setHideSubscriberCheckboxWhenChecked(true));
  };

  const electPlan = () => {
    try {
      const additionalPlanData = additionalElectionData || {};

      const coverageLevelId = handleCoverageLevelId(
        plan.level_id,
        planInfoData,
        selectedCovLevel,
        parentCovLevel,
        isDepSelect
      );

      updateElection(
        buildElectionParams({
          plan,
          levelId: coverageLevelId,
          orgBenefitId,
          enrollmentContextType,
          selectedDependentIds,
          additionalPlanData,
          benefitLookup,
          isFindAProviderEnabled,
          electedRiders,
        })
      );
    } finally {
      resetStateData();
    }
  };

  const handleElection = (
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    setDisplayPopups(true);
    setAdditionalElectionData(additionalPlanData);
  };

  const setPcpElectionData = (data: AdditionalPlanElectionDataDto) => {
    benefitLookup === 'dental' &&
      selected &&
      showEsignatureModal &&
      setDisplayPopups(true);
    const electionData = additionalElectionData || {};
    electionData.pcpFormData = data.pcpFormData;
    setAdditionalElectionData(electionData);
  };

  const setEsignatureElectionData = (data: AdditionalPlanElectionDataDto) => {
    const electionData = additionalElectionData || {};
    electionData.esign_checked = data.esign_checked;
    electionData.esign_password = data.esign_password;
    setAdditionalElectionData(electionData);
    setEsignatureModalRequired(false);
    setDisplayPopups(true);
  };

  // This is acting as our popup queue when electing a plan
  // clicking elect just sets isElecting and the plan is only elected
  // after each required popup is viewed.
  //
  // Current order should be PCP, Plan Popups, then Esignature Modal
  useEffect(() => {
    if (displayPopups) {
      const popupsRequired =
        handlePcpData() || planPopupRequired || handleEsignData();

      if (popupsRequired) {
        if (handlePcpData()) {
          setShowPcpDrawer(true);
        } else if (planPopupRequired) {
          openPlanPopup(orgPlanId);
        } else if (handleEsignData()) {
          openEsignatureModal(orgPlanId);
        } else {
          setIsElecting(true);
        }
      } else {
        setIsElecting(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    additionalElectionData,
    planPopupRequired,
    displayPopups,
    esignatureModalRequired,
  ]);

  const handlePcpData = () => {
    if (additionalElectionData?.pcpFormData) {
      return false;
    }
    return pcpDrawerRequired;
  };

  const handleEsignData = () => {
    if (
      additionalElectionData?.esign_checked ||
      additionalElectionData?.esign_password
    ) {
      return false;
    }
    return esignatureModalRequired;
  };

  // isElecting should only be set after all required popups are viewed
  // so once any necessary popup is viewed we can move forward and elect the plan
  useEffect(() => {
    if (isElecting) {
      electPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isElecting]);

  const btnProps = {
    onClick: () => handleElection(),
  } as BtnProps;

  if (disabled || isElecting) {
    btnProps.disabled = disabled;
  }

  let planEligible = plan.selected_dependents_eligible;

  if (
    isDepSelect &&
    selectedDependentIds.length > 0 &&
    plan?.allowed_dependent_ids?.length > 0
  ) {
    const result = selectedDependentIds.filter((selected) => {
      return plan?.allowed_dependent_ids.find((depId) => depId === selected);
    });
    planEligible = result.length > 0 && plan.selected_dependents_eligible;
  }

  if (!planEligible) {
    btnProps.disabled = true;
  }

  if (plan.level_lookup_code === 'decline' || !plan.subscriber_premium) {
    return <></>;
  }

  const cardStyle = {
    padding: '0px',
  } as Dictionary<string>;

  if (selected || isRecommendedPlan) {
    cardStyle['marginTop'] = '0px';
  }

  let bannerText = '';
  if (selected && preElectedPlan && isRecommendedPlan) {
    bannerText = t('preElectedDecIQSuggestion');
  } else if (selected && preElectedPlan && !isRecommendedPlan) {
    bannerText = t('preElected');
  } else if (selected && !isRecommendedPlan) {
    bannerText = t('chosenPlan');
  } else if (selected && isRecommendedPlan) {
    bannerText = t('chosenDecIQSuggestion');
  } else if (!selected && isRecommendedPlan) {
    bannerText = t('decIQSuggestion');
  }

  let electButtonLabel = t('choosePlan');
  if (confirmButtonOverrideLabel) {
    electButtonLabel = confirmButtonOverrideLabel;
  } else if (preElectedPlan && selected) {
    electButtonLabel = t('keepPlan');
  }

  const showBanner =
    (selected || isRecommendedPlan) &&
    origSelectedCovLevel === selectedCovLevel;

  const showChoosePlanButton =
    preElectedPlan ||
    !selected ||
    origSelectedCovLevel !== selectedCovLevel ||
    Number(plan.org_plan_rider_benefits?.length) > 0; // NaN > 0 is false

  return (
    <div
      id={`plan-card-${plan.plan_id}`}
      className={`col-4 col-md-12 col-lg-12 p-0 ${selected ? 'selected-plan' : ''}`}
    >
      {isRecommendedPlan && (
        <RecommendationBanner
          planName={plan.plan_name}
          hasSelectedDependents={selectedDependentIds.length > 0}
          orgBenefitId={orgBenefitId}
          benefitLookup={benefitLookup}
          dependents={dependents}
          onRecommendationSurveySubmit={onRecommendationSurveySubmit}
        />
      )}
      {oliveRecommends && (
        <OliveRecommends
          reasons={reasons || ''} // pass the reasons here
          drawerOpen={false}
          planName={plan.plan_name} // we will need to pass the actual recommended plan here
          selected={selected}
        />
      )}
      {!oliveRecommends &&
        (showBanner ? (
          <div
            style={{
              display: 'flex',
              backgroundColor: 'var(--branding-primary-900, #212935)',
              padding: '10px',
              justifyContent: 'center',
              borderRadius: '4px 4px 0px 0px',
            }}
            id="selected-indicator"
          >
            {selected && (
              <Check
                size={17}
                color="white"
                aria-hidden="true"
                role="presentation"
              />
            )}{' '}
            <h2>{bannerText}</h2>
          </div>
        ) : (
          <div />
        ))}
      <div className="card planCard" style={cardStyle}>
        <PlanCardHeaderSyled>
          <div className={`icon-container ${color}`}>
            <SpecificIcon benefitType={benefitLookup ? benefitLookup : ''} />
          </div>
          <h3
            className="pl-3_5 mb-0 d-none d-md-flex align-self-center"
            id={`plan-${plan.plan_id}`}
          >
            {plan.plan_name}
          </h3>
          {plan?.carrier_logo_url && plan?.carrier_logo_url !== null ? (
            <CarrierLogoBoxStyled>
              <img
                src={plan.carrier_logo_url}
                alt={plan.carrier || t('defaultLogoAltText')}
                data-testid="carrier-logo"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </CarrierLogoBoxStyled>
          ) : (
            <CarrierLogoBoxStyled>
              <ImageSquare size={32} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{plan.carrier}</Typography>
            </CarrierLogoBoxStyled>
          )}
        </PlanCardHeaderSyled>
        <div className="headline-2 d-md-none text-center mt-2_5 mb-0 mx-4">
          {plan.plan_name}
        </div>
        {planEligible ? (
          <>
            <PlanCardDetails
              benefitLookup={benefitLookup}
              plan={plan}
              subscriber={subscriberProfile}
              dependents={dependents}
              selectedDependentIds={selectedDependentIds}
              pcpMembers={pcpMembers}
              selected={selected}
              electPlan={electPlan}
              setPcpElectionData={setPcpElectionData}
              setAllowPcpElection={setAllowPcpElection}
              togglePcpDrawer={togglePcpDrawer}
              openChooseProviderDrawer={openChooseProviderDrawer}
              setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
              setShowPcpDrawer={setShowPcpDrawer}
              isDepSelect={isDepSelect}
              {...covLevelSelectProps}
              planId={plan?.plan_id}
              isPcpOptional={isPcpOptional}
              isFindAProviderEnabled={isFindAProviderEnabled}
              hideDependentCheckboxWhenChecked={
                hideDependentCheckboxWhenChecked
              }
              setHideDependentCheckboxWhenChecked={
                setHideDependentCheckboxWhenChecked
              }
              subscribeZipCode={subscribeZipCode}
              isEsignPlanRequired={showEsignatureModal || false}
              setPcpDrawerRequired={setPcpDrawerRequired}
              hideEmptyCost={hideEmptyCost}
            />
          </>
        ) : (
          <PlanCardIneligibleBanner data-testid="in-eligible-plan-message">
            <PSBannerWrapper
              id="incomplete-elections-banner"
              message={t('inEligiblePlanBanner')}
              level="info"
              icon
            />
          </PlanCardIneligibleBanner>
        )}
        {plan?.match_level_benefit_message && (
          <PlanCardIneligibleBanner data-testid="in-eligible-plan-message">
            <PSBannerWrapper
              id="incomplete-elections-banner"
              message={plan?.match_level_benefit_message}
              level="info"
              icon
            />
          </PlanCardIneligibleBanner>
        )}
        <RiderBenefits
          riderInfo={plan.org_plan_rider_benefits as PlanRiderDto[]}
          riderCoverages={coverage?.rider_coverages || []}
          planPremium={planPremium}
          electedRiders={electedRiders}
          setElectedRiders={setElectedRiders}
          orgPlanYearId={orgPlanYearId}
          displayCheckbox
          key={plan.plan_name}
          hideEmptyCost={hideEmptyCost}
        />
        <PlanCardFooterStyled className="row">
          {showChoosePlanButton && (
            <div className="col-md text-center text-md-left ">
              <PsButtonTuner
                buttonId={`choose-plan-button_${plan?.plan_id}`}
                label={
                  selectedCovLevel && confirmButtonOverrideLabel && planInfoData
                    ? planInfoData.data.plan_level_costs[
                        `${parentCovLevel || selectedCovLevel}`
                      ]?.level_name
                    : electButtonLabel
                }
                level="primary"
                {...btnProps}
                disabled={
                  plan?.match_level_benefit_message || !planEligible
                    ? true
                    : false
                }
                otherData={[
                  {
                    attribute: 'aria-describedby',
                    value: `plan-${plan.plan_id}`,
                  },
                ]}
              />
            </div>
          )}
          {['medical', 'dental', 'vision'].includes(benefitLookup) &&
            planEligible &&
            !disableCompareBtns && (
              <div className="col-md d-flex justify-content-md-end">
                <AddToCompareStyled>
                  <PsCheckboxTuner
                    data-testid={`checkbox-${plan.plan_id}-${orgBenefitId}`}
                    inputId={`checkbox-${plan.plan_id}-${orgBenefitId}`}
                    name="add_to_compare"
                    label={t('addToCompare')}
                    value={addToCompareCheckbox}
                    onClick={handleCompareCheckbox}
                    checked={addToCompareCheckbox}
                    disabled={disableCompareBtns}
                    otherData={[
                      {
                        attribute: 'aria-describedby',
                        value: `plan-${plan.plan_id}`,
                      },
                    ]}
                  ></PsCheckboxTuner>
                </AddToCompareStyled>
              </div>
            )}
        </PlanCardFooterStyled>
      </div>
      {planEligible && isPcpRequired && showPcpDrawer && (
        <PCPDrawer
          plan={plan}
          benefitLookup={benefitLookup}
          subscriber={subscriberProfile}
          dependents={dependents}
          selectedDependentIds={selectedDependentIds}
          setPcpElectionData={setPcpElectionData}
          pcpMembers={pcpMembers}
          togglePcpDrawer={togglePcpDrawer}
          setDisplayPopups={toggleDisplayPopups}
          showPcpDrawer={showPcpDrawer}
          openChooseProviderDrawer={openChooseProviderDrawer}
          setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
          setShowPcpDrawer={setShowPcpDrawer}
          setPcpDrawerRequired={setPcpDrawerRequired}
          isFindAProviderEnabled={isFindAProviderEnabled}
          hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
          setHideDependentCheckboxWhenChecked={
            setHideDependentCheckboxWhenChecked
          }
          subscribeZipCode={subscribeZipCode}
          allowPcpElection={allowPcpElection}
          electPlan={electPlan}
        />
      )}
      {hasPopupContent && Boolean(showPlanPopup) && (
        <PlanPopup
          planId={plan.plan_id}
          popupContent={popupContentData}
          apiHost={configuration?.apiHost || ''}
          onClose={() => setPlanPopupRequired(false)}
        />
      )}
      {showEsignatureModal && (
        <EsignatureModal
          setEsignElectionData={setEsignatureElectionData}
          carrierLogoUrl={plan?.carrier_logo_url}
          carrierName={plan?.carrier}
          data={plan?.esign_data}
          planId={plan?.plan_id}
          hideChoosePlanButton={true}
          pcpData={plan?.pcp_data}
          setModalRequired={() => {
            toggleDisplayPopups(false);
          }}
        />
      )}
      {selected && !isOnlyPlan && <h2>{t('availablePlans')}</h2>}
    </div>
  );
};
